"use client";

import React, { useState, useRef, useEffect } from 'react';
import { X, MapPin, LocateFixed } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';
import { useAddressManager } from '@/context/AddressManagerContext';
import { getUserAddresses, upsertUserAddress, deleteUserAddress } from '@/lib/supabase/userAddresses';
import { useSession } from '@/context/SessionContext';
import { saveGuestAddressToStorage } from '@/hooks/useGuestAddress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import MapPinSelector from './MapPinSelector';
import LocationAutocomplete from './LocationAutocomplete';
import type { NoteLocation } from '@/types/location';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn, formatAddressForDisplay } from '@/lib/utils';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useScrollLock } from '@/hooks/useScrollLock';

interface Address {
  id: string;
  label: string;
  location: NoteLocation;
  is_primary?: boolean;
  floor?: string | null;
  apartment?: string | null;
  notes?: string | null;
  altura?: string | null;
  streetName?: string | null;
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

const AddressManagerSidebar: React.FC = () => {
  const { close, isOpen, options } = useAddressManager();
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = React.useCallback(() => {
    setIsTyping(false);
    setDeleteCountdown(null);
    setIsClosing(true);
    setTimeout(() => {
      close();
      setIsClosing(false);
    }, 200);
  }, [close]);

  const { session, relevantLocation } = useSession();
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [addressList, setAddressList] = useState<Address[]>([]);
  const [deleteCountdown, setDeleteCountdown] = useState<{ id: string; count: number } | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [mapCoords, setMapCoords] = useState<{lat: number, lon: number} | null>(null);
  const [inputLocation, setInputLocation] = useState<NoteLocation | null>(null);
  const [topInputLocation, setTopInputLocation] = useState<NoteLocation | null>(null);
  const [createdFromTopInput, setCreatedFromTopInput] = useState(false);
  const [autocompleteOpenCycle, setAutocompleteOpenCycle] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [showAlturaError, setShowAlturaError] = useState(false);
  const [showStreetNameError, setShowStreetNameError] = useState(false);
  const { toast } = useToast();
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  // Pull-to-close: ref al ScrollArea
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const storageMode = session?.user?.id ? 'remote' : 'local';
  const { anonymousMode, disableClose, isFirstVisitFlow } = options;

  // Funciones localStorage para modo anónimo
  const saveToLocalStorage = (addresses: Address[]) => {
    try {
      const json = JSON.stringify(addresses);
      localStorage.setItem('pending_addresses', json);
      
      // Verificar que se guardó
      const verify = localStorage.getItem('pending_addresses');
    } catch (error) {
      console.error('[saveToLocalStorage] ERROR saving to localStorage:', error);
    }
  };

  const loadFromLocalStorage = (): Address[] => {
    try {
      const stored = localStorage.getItem('pending_addresses');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  // Cargar direcciones cuando el sidebar se abre
  useEffect(() => {
    if (!isOpen) return;
    
    if (session?.user?.id) {
      // Modo remoto: cargar desde Supabase
      getUserAddresses(session.user.id)
        .then(setAddressList)
        .catch(() => setAddressList([]));
    } else {
      // Modo anónimo: cargar desde localStorage
      const localAddresses = loadFromLocalStorage();
      setAddressList(localAddresses);
    }
  }, [isOpen, session?.user?.id]);

  // Migrar direcciones de localStorage a Supabase cuando el usuario se loguea
  useEffect(() => {
    const migrateLocalAddresses = async () => {
      if (!session?.user?.id) return;
      
      try {
        const localAddresses = loadFromLocalStorage();
        if (!localAddresses || localAddresses.length === 0) {
          return;
        }
        
        
        for (const addr of localAddresses) {
          await upsertUserAddress({
            id: addr.id,
            user_id: session.user.id,
            label: addr.label,
            is_primary: addr.is_primary || false,
            location: addr.location,
            floor: addr.floor || null,
            apartment: addr.apartment || null,
            notes: addr.notes || null,
            altura: addr.altura || null,
            streetName: addr.streetName || null,
          });
        }
        
        // Limpiar localStorage después de migrar exitosamente
        localStorage.removeItem('pending_addresses');
        
        // Recargar direcciones desde Supabase
        const addresses = await getUserAddresses(session.user.id);
        setAddressList(addresses);
        
        window.dispatchEvent(new Event('address-updated'));
      } catch (error) {
        console.error('[AddressManager] Error migrating local addresses:', error);
      }
    };
    
    migrateLocalAddresses();
  }, [session?.user?.id]);

  // Recalcular medidas cuando el sidebar se abre
  useEffect(() => {
    if (isOpen) {
      setAutocompleteOpenCycle((prev) => prev + 1);
    }
  }, [isOpen]);

  // Recalcular medidas cuando el sidebar se abre
  useEffect(() => {
    if (isOpen) {
      recalculate();
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, recalculate]);

  // Desactivar completamente el cierre por click fuera o pull-to-close durante edición
  // Solo permitir cierre por botón de cerrar, guardar o seleccionar dirección
  // (No se agrega ningún event listener de click-outside)

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Recalcular etiqueta editable de direccion en tiempo real (sin tocar displayName geocodificado)
  useEffect(() => {
    if (!editingAddress) return;
    const nextLabel = buildAddressDisplayName(editingAddress, inputLocation?.displayName);
    if (nextLabel !== editingAddress.label) {
      setEditingAddress(prev => (prev ? { ...prev, label: nextLabel } : prev));
    }
  }, [editingAddress?.streetName, editingAddress?.altura, editingAddress?.floor, editingAddress?.apartment, editingAddress?.id, inputLocation?.displayName]);

  useEffect(() => {
    if (!deleteCountdown || deleteCountdown.count <= 0) return;
    const timer = setTimeout(() => {
      setDeleteCountdown((prev) => (prev ? { ...prev, count: prev.count - 1 } : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [deleteCountdown]);

  const getRelevantCityCoords = () => {
    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      if (profile.relevant_latitude && profile.relevant_longitude) {
        return { lat: profile.relevant_latitude, lon: profile.relevant_longitude };
      }
    } catch {}
    return { lat: -24.7821, lon: -65.4069 };
  };

  const getNavbarCityPlaceholder = () => {
    const cityFromSession = String((relevantLocation as any)?.city || '').trim();
    if (cityFromSession) return cityFromSession;

    const displayFromSession = String(relevantLocation?.displayName || '').trim();
    if (displayFromSession) return displayFromSession;

    try {
      const raw = localStorage.getItem('relevant_location');
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      const city = String(parsed?.city || '').trim();
      if (city) return city;
      return String(parsed?.displayName || '').trim();
    } catch {
      return '';
    }
  };

  const extractAddressComponents = (displayName: string | undefined) => {
    if (!displayName) return { altura: '', streetName: '' };
    
    // Split by comma
    const parts = displayName.split(',').map(p => p.trim()).filter(Boolean);
    
    // Check if the FIRST part contains a number (e.g., "Caseros 2000")
    // or if the first part IS a number and the second is the street
    let altura = '';
    let streetName = '';

    if (parts.length > 0) {
      const firstPart = parts[0];
      // Regex to find trailing numbers (e.g., "Caseros 2000" -> "2000")
      const match = firstPart.match(/^(.*?)\s+(\d+)$/);
      if (match) {
        streetName = match[1];
        altura = match[2];
      } else if (/^\d+$/.test(firstPart) && parts.length > 1) {
        // If first part is ONLY digits, assume it's the number (OSM format "2000, Caseros, ...")
        altura = firstPart;
        streetName = parts[1];
      } else {
        // Fallback: use first part as street if no number found
        streetName = firstPart;
        // Check if second part is just a number (altura)
        if (parts.length > 1 && /^\d+$/.test(parts[1])) {
          altura = parts[1];
        }
      }
    }
    
    return { altura, streetName };
  };

  const buildAddressDisplayName = (
    addr: Pick<Address, 'streetName' | 'altura' | 'floor' | 'apartment' | 'label'>,
    fallback?: string
  ) => {
    const street = String(addr.streetName || '').trim();
    const number = String(addr.altura || '').trim();
    const floor = String(addr.floor || '').trim();
    const depto = String(addr.apartment || '').trim();

    const baseLine = [
      street,
      number,
      floor ? `Piso ${floor}` : '',
      depto ? `Depto ${depto}` : '',
    ].filter(Boolean).join(' ').trim();

    const fallbackText = String(addr.label || fallback || '').trim();
    const fallbackParts = fallbackText.split(',').map((part) => part.trim()).filter(Boolean);
    const fallbackRemainder = fallbackParts.length > 1 ? fallbackParts.slice(1).join(', ') : '';

    const normalize = (value: string) => value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const normalizedBase = normalize(baseLine);
    const normalizedRemainder = normalize(fallbackRemainder);
    const includeRemainder = Boolean(baseLine && fallbackRemainder && normalizedRemainder && !normalizedBase.includes(normalizedRemainder));

    const fullAddress = [baseLine, includeRemainder ? fallbackRemainder : ''].filter(Boolean).join(', ').trim() || fallbackText;
    return formatAddressForDisplay(fullAddress);
  };

  const reverseGeocode = async (lat: number, lon: number) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`);
      const data = await response.json();
      if (data && data.address) {
        return {
          road: data.address.road || data.address.pedestrian || data.address.street || '',
          houseNumber: data.address.house_number || ''
        };
      }
    } catch (error) {
      console.error('[AddressManager] Error in reverseGeocode:', error);
    }
    return null;
  };

  const handleSetPrimary = async (id: string) => {
    if (addressList.length === 0) return;
    if (isSavingAddress) return;
    
    // Encontrar la dirección seleccionada
    const selectedAddr = addressList.find(addr => addr.id === id);
    if (!selectedAddr) return;
    
    // Crear nueva lista: la seleccionada al inicio, las demás después
    const otherAddresses = addressList.filter(addr => addr.id !== id).map(addr => ({ ...addr, is_primary: false }));
    const newList = [
      { ...selectedAddr, is_primary: true },
      ...otherAddresses
    ];
    
    // Actualizar UI optimísticamente
    setAddressList(newList);

    // Si no está logueado, guardar en localStorage
    if (!session?.user?.id) {
      saveGuestAddressToStorage(selectedAddr);
      window.dispatchEvent(new Event('address-updated'));
      return;
    }

    // Si está logueado, persistir en Supabase
    setIsSavingAddress(true);
    try {
      // Primero, actualizar todas las direcciones a is_primary: false
      await Promise.all(otherAddresses.map(async (addr) => {
        const canonicalDisplayName = buildAddressDisplayName(addr, addr.location?.displayName);
        return upsertUserAddress({
          id: addr.id,
          user_id: session.user.id,
          label: canonicalDisplayName,
          is_primary: false,
          location: {
            ...addr.location,
            displayName: canonicalDisplayName,
          },
          floor: addr.floor || null,
          apartment: addr.apartment || null,
          notes: addr.notes || null,
          altura: addr.altura || null,
          streetName: addr.streetName || null,
        });
      }));
      
      // Luego, actualizar la dirección seleccionada a is_primary: true
      const selectedCanonicalDisplayName = buildAddressDisplayName(selectedAddr, selectedAddr.location?.displayName);
      await upsertUserAddress({
        id: selectedAddr.id,
        user_id: session.user.id,
        label: selectedCanonicalDisplayName,
        is_primary: true,
        location: {
          ...selectedAddr.location,
          displayName: selectedCanonicalDisplayName,
        },
        floor: selectedAddr.floor || null,
        apartment: selectedAddr.apartment || null,
        notes: selectedAddr.notes || null,
        altura: selectedAddr.altura || null,
        streetName: selectedAddr.streetName || null,
      });
      
      window.dispatchEvent(new Event('address-updated'));
    } catch (e: any) {
      toast({ title: 'Error al actualizar', description: e?.message || 'No se pudo cambiar la dirección principal.', variant: 'destructive' });
      // Revertir cambios si falla
      try {
        const refreshed = await getUserAddresses(session.user.id);
        setAddressList(refreshed as any);
      } catch (_) {}
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleAdd = () => {
    // Usar la ubicación relevante actual si está disponible
    const initialLocation = relevantLocation ? 
      {
        displayName: relevantLocation.displayName,
        latitude: relevantLocation.latitude,
        longitude: relevantLocation.longitude
      }
      : { displayName: '', latitude: 0, longitude: 0 };
    
    // Obtener datos del perfil si existen
    const salutation = (session?.user as any)?.user_metadata?.salutation || '';
    const firstName = (session?.user as any)?.user_metadata?.first_name || '';
    const lastName = (session?.user as any)?.user_metadata?.last_name || '';
    
    setEditingAddress({ 
      id: '', 
      label: '', 
      location: initialLocation, 
      is_primary: true, 
      floor: '', 
      apartment: '', 
      notes: '',
      salutation: salutation || null,
      firstName: firstName || null,
      lastName: lastName || null
    });
    setInputLocation(relevantLocation || null);
    setMapCoords(relevantLocation ? { lat: relevantLocation.latitude, lon: relevantLocation.longitude } : getRelevantCityCoords());
    setIsLocationModalOpen(true);
    setCreatedFromTopInput(false);
  };

  const handleEdit = async (address: Address) => {
    // 1. Iniciar edición con datos básicos
    setEditingAddress({
      ...address,
      altura: address.altura || '',
      streetName: address.streetName || '',
      floor: address.floor || '',
      apartment: address.apartment || '',
      notes: address.notes || '',
      salutation: address.salutation || null,
      firstName: address.firstName || null,
      lastName: address.lastName || null,
    });
    setInputLocation(address.location);
    setMapCoords({ lat: address.location.latitude, lon: address.location.longitude });
    setIsLocationModalOpen(true);
    setCreatedFromTopInput(false);

    // 2. Si faltan calle o altura, intentar recuperarlos por reverse geocoding
    if (!address.streetName || !address.altura) {
      const components = await reverseGeocode(address.location.latitude, address.location.longitude);
      if (components) {
        setEditingAddress(prev => {
          if (!prev || prev.id !== address.id) return prev;
          return {
            ...prev,
            streetName: prev.streetName || components.road || '',
            altura: prev.altura || components.houseNumber || ''
          };
        });
      }
    }
  };

  const executeDelete = async (id: string) => {
    // Optimistically remove from UI
    const filtered = addressList.filter(addr => addr.id !== id);
    let primaryIdx = filtered.findIndex(addr => addr.is_primary);
    if (primaryIdx === -1 && filtered.length > 0) primaryIdx = 0;
    const cleaned = filtered.map((addr) => ({ ...addr, is_primary: false }));
    if (filtered.length > 0) cleaned[primaryIdx] = { ...cleaned[primaryIdx], is_primary: true };
    setAddressList(cleaned);
    // call supabase delete immediately
    try {
      await deleteUserAddress(id);
    } catch (e: any) {
      toast({ title: 'Error al eliminar', description: e?.message || 'No se pudo eliminar la dirección.', variant: 'destructive' });
      // revert: refetch addresses if session available, else put back previous list
      if (session?.user?.id) {
        try {
          const refreshed = await getUserAddresses(session.user.id);
          setAddressList(refreshed as any);
        } catch (_) {
          // nothing
        }
      }
    } finally {
      setDeleteCountdown(null);
    }
  };

  useEffect(() => {
    if (deleteCountdown?.count === 0) {
      const id = deleteCountdown.id;
      setDeleteCountdown(null);
      void executeDelete(id);
    }
  }, [deleteCountdown?.count]);

  const handleSaveLocation = async (location: NoteLocation) => {
    if (!editingAddress) return;
    if (isSavingAddress) return;
    setIsSavingAddress(true);
    const computedLabel = buildAddressDisplayName(editingAddress, location.displayName);
    const canonicalLocation: NoteLocation = {
      ...location,
      displayName: computedLabel,
    };

    // Build updated list optimistically
    let updatedList = [...addressList];
    const isNew = !editingAddress.id;
    const uuid = isNew ? (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Math.random()}-${Date.now()}`) : editingAddress.id;
    const tempAddr: Address = { ...editingAddress, id: uuid, location: canonicalLocation, label: computedLabel } as Address;

    if (isNew) {
      // Si es nueva, establecerla como primaria y quitar primaria a las demás
      updatedList = updatedList.map(addr => ({ ...addr, is_primary: false }));
      updatedList.unshift({ ...tempAddr, is_primary: true });
      // Limitar a 10 direcciones máximo
      if (updatedList.length > 10) {
        const removedAddress = updatedList.pop();
        // Si tiene ID, eliminarla de Supabase también
        if (removedAddress?.id && session?.user?.id) {
          try {
            await deleteUserAddress(removedAddress.id);
          } catch (e) {
            console.error('Error al eliminar dirección excedente:', e);
          }
        }
      }
    } else {
      updatedList = updatedList.map(addr => addr.id === editingAddress.id ? tempAddr : addr);
    }

    setAddressList(updatedList);
    setIsLocationModalOpen(false);
    setEditingAddress(null);
    setMapCoords(null);
    setInputLocation(null);
    setIsTyping(false);

    // Guardar según el modo de almacenamiento
    if (storageMode === 'local') {
      // Modo anónimo: guardar en localStorage
      saveToLocalStorage(updatedList);
      
      // También guardar la dirección primaria (la nueva dirección será siempre primaria en este caso)
      if (isNew) {
        saveGuestAddressToStorage(tempAddr);
      }
      
      // Si es el flujo de primera visita, emitir evento
      if (isFirstVisitFlow) {
        window.dispatchEvent(new CustomEvent('address-confirmed-first-time', { detail: { address: tempAddr } }));
      } else {
        // Solo mostrar toast si NO es el flujo de primera visita (evitar ruido visual en el orchestrator)
        toast({ title: 'Dirección guardada', description: 'Podés explorar contenido cercano. Registrate para guardar tus datos.' });
      }
      setIsSavingAddress(false);
      return;
    }

    // If we have a session, persist immediately to Supabase
    if (!session?.user?.id) {
      setIsSavingAddress(false);
      return;
    }

    try {
      // Si es una dirección nueva, primero actualizar todas las existentes a is_primary: false
      if (isNew) {
        const existingAddresses = addressList.filter(addr => addr.id && addr.id !== uuid);
        await Promise.all(existingAddresses.map(async (addr) => {
          return upsertUserAddress({
            id: addr.id,
            user_id: session.user.id,
            label: buildAddressDisplayName(addr, addr.location?.displayName),
            is_primary: false,
            location: {
              ...addr.location,
              displayName: buildAddressDisplayName(addr, addr.location?.displayName),
            },
            altura: addr.altura || null,
            streetName: addr.streetName || null,
            floor: addr.floor || null,
            apartment: addr.apartment || null,
            notes: addr.notes || null,
            salutation: addr.salutation || null,
            firstName: addr.firstName || null,
            lastName: addr.lastName || null,
          });
        }));
      }

      const payload = {
        id: isNew ? uuid : editingAddress.id,
        user_id: session.user.id,
        label: computedLabel,
        is_primary: isNew ? true : !!(editingAddress as any).is_primary,
        location: canonicalLocation,
        altura: (editingAddress as any).altura || null,
        streetName: (editingAddress as any).streetName || null,
        floor: (editingAddress as any).floor || null,
        apartment: (editingAddress as any).apartment || null,
        notes: (editingAddress as any).notes || null,
        salutation: (editingAddress as any).salutation || null,
        firstName: (editingAddress as any).firstName || null,
        lastName: (editingAddress as any).lastName || null,
      } as any;
      const saved = await upsertUserAddress(payload);
      
      window.dispatchEvent(new Event('address-updated'));
      
      // Si es nueva, actualizar el estado con todas las direcciones correctas
      if (isNew) {
        setAddressList(prev => prev.map(a => 
          a.id === uuid 
            ? { id: saved.id, label: saved.label, location: saved.location, is_primary: saved.is_primary, floor: saved.floor, apartment: saved.apartment, notes: saved.notes, salutation: saved.salutation, firstName: saved.firstName, lastName: saved.lastName }
            : { ...a, is_primary: false }
        ));
      } else {
        setAddressList(prev => prev.map(a => a.id === editingAddress.id ? { id: saved.id, label: saved.label, location: saved.location, is_primary: saved.is_primary, floor: saved.floor, apartment: saved.apartment, notes: saved.notes, salutation: saved.salutation, firstName: saved.firstName, lastName: saved.lastName } : a));
      }
      
      setTopInputLocation(null);
      setCreatedFromTopInput(false);
    } catch (e: any) {
      console.error('Error saving address:', e);
      toast({ title: 'Error al guardar', description: e?.message || 'No se pudo guardar la dirección.', variant: 'destructive' });
      // NO refrescar desde servidor en error, mantener los cambios locales
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleSaveAll = async () => {
    const invalid = addressList.find(addr => !addr.location || addr.location.latitude == null || addr.location.longitude == null || isNaN(addr.location.latitude) || isNaN(addr.location.longitude) || addr.location.latitude === 0 || addr.location.longitude === 0);
    if (invalid) { toast({ title: 'Error al guardar', description: 'Alguna dirección no tiene una ubicación válida.', variant: 'destructive' }); return; }
    let primaryIdx = addressList.findIndex(addr => addr.is_primary);
    const cleaned = addressList.map((addr, idx) => ({ ...addr, is_primary: false }));
    if (addressList.length > 0) cleaned[primaryIdx] = { ...cleaned[primaryIdx], is_primary: true };
    handleClose();
  };

  // Desactivar pull-to-close completamente
  usePullToClose(scrollAreaRef, {
    onClose: () => {},
    threshold: 9999,
    enabled: false
  });

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      {(isOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-transparent z-[9999] transition-opacity duration-200',
            isClosing && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        />
      )}

      <div
      ref={sidebarRef}
          className={cn(
            'fixed right-0 w-full lg:w-[400px] bg-background z-[10000] flex flex-col',
        'transition-transform duration-200',
        isOpen && !isClosing ? 'translate-x-0' : 'translate-x-full'
      )}
      style={{
        top: isDesktop ? `${headerHeight}px` : '0',
        height: isDesktop 
          ? `calc(100vh - ${headerHeight}px)` 
          : '100dvh',
        bottom: !isDesktop ? 0 : undefined,
        pointerEvents: (isOpen && !isClosing) ? 'auto' : 'none',
        paddingBottom: !isDesktop ? '0' : '0'
      }}
    >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground">
        <h2 className="text-lg font-bold flex items-center gap-2 text-primary-foreground">
          <MapPin className="w-5 h-5" />
          {isFirstVisitFlow ? 'Establece tu ubicación' : 'Tu ubicación'}
        </h2>
        <Button variant="ghost" size="icon" onClick={handleClose} className="text-primary-foreground hover:bg-primary-foreground/10" aria-label="Cerrar">
          <X className="w-5 h-5" />
        </Button>
      </div>
      <ScrollArea ref={scrollAreaRef} className="flex-1 w-full" viewportClassName="max-h-full overflow-y-auto">
        <div className="p-4 space-y-3">
          {!isLocationModalOpen && (
            <>
              <LocationAutocomplete
                value={topInputLocation}
                onChange={(loc) => {
                  setTopInputLocation(loc);
                  if (loc) {
                    // Extraer altura y streetName basándose en el resultado
                    const { altura, streetName } = loc.road || loc.houseNumber
                      ? { altura: loc.houseNumber || '', streetName: loc.road || '' }
                      : extractAddressComponents(loc.displayName);

                    // Open inline editor populated from top input
                    const newAddr: Address = { 
                      id: '', 
                      label: loc.displayName, 
                      location: loc, 
                      is_primary: true, 
                      floor: '', 
                      apartment: '', 
                      notes: '',
                      altura: altura,
                      streetName: streetName
                    };
                    setEditingAddress(newAddr);
                    setInputLocation(loc);
                    // Refuerzo: si loc no tiene lat/lon válidos, no abrir el modal
                    if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number' && !isNaN(loc.latitude) && !isNaN(loc.longitude)) {
                      setMapCoords({ lat: loc.latitude, lon: loc.longitude });
                      setIsLocationModalOpen(true);
                      setCreatedFromTopInput(true);
                    } else {
                      setMapCoords(null);
                      setIsLocationModalOpen(false);
                      setEditingAddress(null);
                      setInputLocation(null);
                      setCreatedFromTopInput(false);
                      setTopInputLocation(null);
                      toast({ title: 'Error', description: 'La dirección seleccionada no tiene coordenadas válidas.', variant: 'destructive' });
                    }
                  }
                  if (!loc) {
                    // cleared
                    setIsLocationModalOpen(false);
                    setEditingAddress(null);
                    setMapCoords(null);
                    setInputLocation(null);
                    setCreatedFromTopInput(false);
                    setIsTyping(false);
                  }
                }}
                onInputChange={(value) => {
                  setIsTyping(value.length > 0);
                }}
                allowAnyPlace={true}
                showGps={false}
                showNotFoundButton={true}
                onNotFound={() => {
                  // Abrir modal vacío para que el usuario ingrese dirección personalizada
                  setEditingAddress({ 
                    id: '', 
                    label: '', 
                    location: { displayName: '', latitude: 0, longitude: 0 }, 
                    is_primary: false, 
                    floor: '', 
                    apartment: '', 
                    notes: '' 
                  });
                  setInputLocation(null);
                  setMapCoords(null);
                  setIsLocationModalOpen(true);
                  setCreatedFromTopInput(false);
                  setTopInputLocation(null);
                  setIsTyping(false);
                }}
              />
              {/* Card GPS debajo del input, igual a las direcciones */}
              {!isTyping && (
                <button
                  type="button"
                  className="flex items-center border rounded p-2 min-h-[56px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2"
                  onClick={async () => {
                    if (!('geolocation' in navigator)) {
                  window.alert('Tu navegador no soporta geolocalización.');
                  return;
                }
                navigator.geolocation.getCurrentPosition(
                  async (pos) => {
                    try {
                      const p = pos.coords;
                      const r = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${p.latitude}&lon=${p.longitude}&zoom=18&addressdetails=1`,
                        { mode: 'cors' }
                      );
                      if (!r.ok) throw new Error('reverse failed');
                        const j = await r.json();
                        const display_raw = j.display_name || '';
                        const addr = j.address || {};
                        const road = addr.road || addr.street || addr.pedestrian || addr.cycleway || '';
                        const houseNumber = addr.house_number || addr.street_number || addr.addr_street_number || '';
                        
                        // Mostrar toda la dirección en el input
                        const newLocation = { 
                          displayName: display_raw, 
                          latitude: p.latitude, 
                          longitude: p.longitude,
                          road,
                          houseNumber
                        };
                        setTopInputLocation(newLocation);
                        // Trigger el flujo de onChange
                        if (newLocation) {
                          const newAddr = { 
                            id: '', 
                            label: newLocation.displayName, 
                            location: newLocation, 
                            is_primary: true, 
                            floor: '', 
                            apartment: '', 
                            notes: '',
                            altura: houseNumber,
                            streetName: road
                          };
                          setEditingAddress(newAddr);
                        setInputLocation(newLocation);
                        setMapCoords({ lat: newLocation.latitude, lon: newLocation.longitude });
                        setIsLocationModalOpen(true);
                        setCreatedFromTopInput(true);
                      }
                    } catch (e) {
                      window.alert('No se pudo obtener la dirección cercana.');
                    }
                  },
                  (err) => {
                    window.alert('No fue posible obtener la ubicación.');
                  },
                  { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                );
              }}
            >
              <LocateFixed className="h-5 w-5 text-primary" />
              <span className="font-bold text-sm">Tu ubicación actual</span>
            </button>
          )}
            </>
          )}
          
          {/* Mostrar formulario de edición cuando isLocationModalOpen y editingAddress están activos */}
          {isLocationModalOpen && editingAddress && (
            <>
              {mapCoords && (
                <div className="rounded-lg overflow-hidden border" style={{ height: 250 }}>
                  <MapPinSelector
                    lat={mapCoords.lat}
                    lon={mapCoords.lon}
                    onChange={(lat, lon) => {
                      // Solo actualizar mapCoords, NO inputLocation
                      // Esto evita que el mapa sobrescribe cambios manuales en el input
                      setMapCoords({ lat, lon });
                    }}
                    height="250px"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className={showStreetNameError ? 'text-primary font-bold' : ''}>Nombre de la calle *</Label>
                  <Input 
                    onMouseDown={(e) => e.stopPropagation()} 
                    value={editingAddress?.streetName || ''} 
                    onChange={(e) => {
                      setEditingAddress(ed => ed ? { ...ed, streetName: e.target.value } : ed);
                      if (e.target.value.trim()) setShowStreetNameError(false);
                    }} 
                    placeholder="Ej: Juan Gregorio de las Heras"
                    className={showStreetNameError ? 'border-primary border-2 animate-pulse' : ''}
                  />
                </div>
                <div>
                  <Label className={showAlturaError ? 'text-primary font-bold' : ''}>Altura *</Label>
                  <Input 
                    onMouseDown={(e) => e.stopPropagation()} 
                    value={editingAddress?.altura || ''} 
                    onChange={(e) => {
                      setEditingAddress(ed => ed ? { ...ed, altura: e.target.value } : ed);
                      if (e.target.value.trim()) setShowAlturaError(false);
                    }} 
                    placeholder="Ej: 8"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={showAlturaError ? 'border-primary border-2 animate-pulse' : ''}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label>Piso</Label>
                  <Input onMouseDown={(e) => e.stopPropagation()} value={editingAddress?.floor || ''} onChange={(e) => setEditingAddress(ed => ed ? { ...ed, floor: e.target.value } : ed)} />
                </div>
                <div>
                  <Label>Depto</Label>
                  <Input onMouseDown={(e) => e.stopPropagation()} value={editingAddress?.apartment || ''} onChange={(e) => setEditingAddress(ed => ed ? { ...ed, apartment: e.target.value } : ed)} />
                </div>
              </div>

              <div className="mt-2">
                <Label>Notas</Label>
                <Textarea onMouseDown={(e) => e.stopPropagation()} value={editingAddress?.notes || ''} onChange={(e) => setEditingAddress(ed => ed ? { ...ed, notes: e.target.value } : ed)} />
              </div>
            </>
          )}

          {/* Mostrar lista de direcciones solo si NO estamos editando */}
          <div className="flex flex-col gap-3">
            {!isLocationModalOpen && !isTyping && addressList.map((addr, index) => (
              <motion.div 
                key={addr.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ 
                  layout: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                  y: { duration: 0.3, delay: index * 0.05 }
                }}
                className={`flex items-center justify-between gap-2 border rounded p-2.5 min-h-[64px] transition-colors ${!addr.is_primary ? 'cursor-pointer hover:bg-muted' : ''}`}
                onClick={() => !addr.is_primary && handleSetPrimary(addr.id)}
              >
                <div className="flex-1 min-w-0">
                  {(() => {
                    const canonicalText = formatAddressForDisplay(String(addr.location?.displayName || addr.label || '').trim());

                    return (
                      <>
                        <div className="font-bold text-sm leading-tight break-words whitespace-normal">{canonicalText}</div>
                      </>
                    );
                  })()}
                </div>
                <div className="flex gap-2 items-center shrink-0">
                  {addr.is_primary && (
                    <span className="text-xs text-green-700 font-semibold">Actualmente</span>
                  )}
                  {session?.user?.id && (
                    <>
                      {!addr.is_primary && (
                        deleteCountdown?.id === addr.id ? (
                          <button
                            type="button"
                            className="h-9 w-9 min-w-9 p-0 rounded-full shadow-md bg-destructive border-0 flex items-center justify-center shrink-0 aspect-square text-destructive-foreground font-bold text-base"
                            onClick={(e) => { e.stopPropagation(); setDeleteCountdown(null); }}
                            aria-label="Cancelar eliminación"
                            title="Click para cancelar"
                          >
                            {deleteCountdown.count}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="h-9 w-9 min-w-9 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteCountdown({ id: addr.id, count: 3 });
                            }}
                            aria-label="Eliminar dirección"
                            title="Eliminar dirección"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )
                      )}
                    </>
                  )}
                </div>
              </motion.div>
          ))}
          </div>

          {/* Safe Area Spacer for Mobile Bottom */}
          {!isDesktop && <div className="h-[env(safe-area-inset-bottom,0px)] w-full shrink-0" />}
        </div>
      </ScrollArea>

      {/* Footer - Show conditional buttons only when editing */}
      {isLocationModalOpen && editingAddress && (
        <div className="border-t pt-4 px-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-2 bg-muted/20 shrink-0">
          <Button 
            size="lg"
            className="w-full font-bold"
            disabled={isSavingAddress}
            onClick={() => { 
              if (isSavingAddress) return;
              if (!inputLocation || !mapCoords) return; 
              
              if (!editingAddress?.streetName?.trim()) {
                setShowStreetNameError(true);
                toast({
                  title: "Nombre de calle requerido",
                  description: "Por favor ingresa el nombre de la calle.",
                  variant: "destructive"
                });
                return;
              }
              
              if (!editingAddress?.altura?.trim()) {
                setShowAlturaError(true);
                toast({
                  title: "Altura requerida",
                  description: "Por favor ingresa la numeración de la calle.",
                  variant: "destructive"
                });
                return;
              }
              
              const finalDisplayName = buildAddressDisplayName(editingAddress, inputLocation.displayName);
              
              const finalLocation = {
                ...inputLocation,
                displayName: finalDisplayName,
                latitude: mapCoords.lat,
                longitude: mapCoords.lon
              };
              
              handleSaveLocation(finalLocation); 
              setTopInputLocation(null); 
              setCreatedFromTopInput(false); 
            }}
          >
            {isSavingAddress ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            className="w-full"
            disabled={isSavingAddress}
            onClick={() => { 
              setIsLocationModalOpen(false); 
              setEditingAddress(null); 
              setMapCoords(null); 
              setInputLocation(null); 
              setTopInputLocation(null); 
              setCreatedFromTopInput(false); 
              setIsTyping(false);
              setShowAlturaError(false); 
              setShowStreetNameError(false); 
            }}
          >
            Cancelar
          </Button>
        </div>
      )}
    </div>
    </>
  );
};

export default AddressManagerSidebar;
